import { useState } from "react";
import { X, AlertTriangle, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCHF, fmtDate, formatApiError } from "@/lib/api";
import { OPTION_LABELS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";

export default function ResolveDialog({ doc, onClose, onResolved }) {
  const [values, setValues] = useState({});
  const [deferred, setDeferred] = useState({});
  const [reason, setReason] = useState("");
  const [showOverride, setShowOverride] = useState(false);
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!doc) return null;
  const pending = doc.pending_fields || [];
  const issues = doc.issues || [];
  const isDup = issues.some((i) => i.code?.startsWith("duplicate"));

  const setVal = (f, v) => { setValues((s) => ({ ...s, [f]: v })); setDeferred((s) => ({ ...s, [f]: false })); };
  const setDef = (f, v) => setDeferred((s) => ({ ...s, [f]: v }));

  const openExisting = async () => {
    try {
      const { data } = await api.get(`/documents/${doc.duplicate_of}`);
      setExisting(data);
    } catch { toast.error("Originale non trovato"); }
  };

  const submit = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.doc_id}/resolve`, payload);
      if (data.status === "archived") toast.success("Eccezione risolta. Documento verificato e registrato.");
      else if (data.status === "discarded") toast.success("Acquisizione ignorata. Resta solo l'originale esistente.");
      else toast.success("Decisione registrata. I campi rimandati restano aperti in coda.");
      onResolved?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const anyInput = Object.keys(values).length > 0 || Object.values(deferred).some(Boolean);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="resolve-dialog" onClick={onClose}>
      <div className="glass-strong rounded-3xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display font-bold text-lg">Completa verifica</h2>
            <p className="text-xs text-gray-500 mt-0.5">{doc.supplier || doc.filename} · {doc.doc_type} · <span className="font-mono">{fmtCHF(doc.amount)}</span></p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300" data-testid="resolve-close"><X size={18} /></button>
        </div>

        {issues.length > 0 && (
          <div className="mb-4 space-y-1.5" data-testid="resolve-issues">
            {issues.map((i, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs text-amber-300/90 bg-amber-400/5 border border-amber-400/15 rounded-xl px-3 py-2">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {i.message}
              </div>
            ))}
          </div>
        )}

        {isDup && (
          <div className="glass rounded-2xl p-4 mb-4 border !border-red-400/25" data-testid="resolve-duplicate-box">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-300 mb-2"><Copy size={14} /> Duplicato rilevato</div>
            <p className="text-xs text-gray-400 mb-3 leading-relaxed">
              Un documento identico esiste già. Non viene mai creata una seconda registrazione senza una tua decisione esplicita.
            </p>
            {existing && (
              <div className="rounded-xl border border-white/10 px-3 py-2.5 mb-3 text-xs" data-testid="resolve-existing-doc">
                <div className="text-gray-200">{existing.supplier || existing.filename} · {fmtDate(existing.date)} · <span className="font-mono">{fmtCHF(existing.amount)}</span></div>
                <div className="text-gray-500 mt-0.5 font-mono break-all">sha256: {existing.storage_ref?.checksum?.slice(0, 24)}…</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="resolve-open-existing" onClick={openExisting} className="btn-ghost !text-xs">
                <ExternalLink size={12} /> Apri originale esistente
              </button>
              <button data-testid="resolve-discard" disabled={busy}
                onClick={() => submit({ discard: true })} className="btn-ghost !text-xs !border-red-400/30 !text-red-300">
                Ignora nuova acquisizione
              </button>
            </div>
            <button onClick={() => setShowOverride((s) => !s)} className="text-[11px] text-gray-500 hover:text-gray-300 mt-3" data-testid="resolve-override-toggle">
              Override eccezionale (richiede motivazione)…
            </button>
            {showOverride && (
              <textarea data-testid="resolve-override-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                className="input-dark mt-2 !text-xs" rows={2} placeholder="Motivazione registrata nell'audit trail…" />
            )}
          </div>
        )}

        {pending.length > 0 && (
          <div className="space-y-4 mb-4" data-testid="resolve-fields">
            {pending.map((pf) => (
              <div key={pf.field} data-testid={`resolve-field-${pf.field}`}>
                <div className="text-sm text-gray-200 font-medium mb-2">{pf.question}</div>
                {pf.type === "choice" ? (
                  <div className="flex flex-wrap gap-1.5">
                    {pf.options.map((opt) => (
                      <button key={opt} data-testid={`resolve-opt-${pf.field}-${opt}`}
                        onClick={() => setVal(pf.field, opt)}
                        className={`chip !cursor-pointer !py-2 transition-colors ${values[pf.field] === opt ? "!text-cyan-200 !border-cyan-400/60 !bg-cyan-400/15" : ""}`}>
                        {OPTION_LABELS[opt] || opt}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input data-testid={`resolve-input-${pf.field}`} type={pf.type === "number" ? "number" : pf.type === "date" ? "date" : "text"}
                    value={values[pf.field] || ""} onChange={(e) => setVal(pf.field, e.target.value)}
                    className="input-dark !py-2.5 text-sm" step="0.05" />
                )}
                <label className="flex items-center gap-2 mt-2 text-[11px] text-gray-500 cursor-pointer">
                  <input type="checkbox" data-testid={`resolve-defer-${pf.field}`} checked={!!deferred[pf.field]}
                    onChange={(e) => setDef(pf.field, e.target.checked)} className="accent-cyan-400" />
                  Non lo so — verifica successivamente (resta in coda, non verificato)
                </label>
              </div>
            ))}
          </div>
        )}

        {pending.length === 0 && !isDup && (
          <div className="mb-4">
            <SectionTitle>Motivazione accettazione (registrata in audit)</SectionTitle>
            <textarea data-testid="resolve-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              className="input-dark !text-xs" rows={2} placeholder="Es. documento verificato a vista con l'originale cartaceo…" />
          </div>
        )}

        <div className="flex gap-2">
          <button data-testid="resolve-submit" disabled={busy || (!anyInput && !reason.trim() && pending.length === 0 && !isDup)}
            onClick={() => submit({ resolutions: values, deferred: Object.keys(deferred).filter((k) => deferred[k]),
                                    override_duplicate: showOverride && !!reason.trim(), reason })}
            className="btn-primary flex-1 !py-3">
            {isDup && !showOverride ? "Risolvi duplicato" : pending.some((p) => p.field === "payment_method") && !values.payment_method ? "Indica metodo di pagamento" : "Completa verifica"}
          </button>
        </div>
        <p className="text-[10px] text-gray-600 mt-3 text-center">
          Ogni decisione è registrata: campo, valore precedente/nuovo, utente, data · fonte: conferma umana.
        </p>
      </div>
    </div>
  );
}
